/**
 * TargetPaceLifebar — at-a-glance "are we landing the period or not?"
 *
 * Renders for any division that has a target + actual on
 * /api/divisions/:divId/quarter-stats. Pulls trailing-7d pace and
 * projected close from /api/tdb/leader-pulse when divisionId === 'tdb'
 * (so the same component degrades to the lighter pace view for other
 * divisions until they wire equivalent endpoints).
 *
 * Visual:
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ Q1 Pace · Behind · projected ₹41.79L vs target ₹56.25L          │
 *   │ ████████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░  ▲ should-be-here   │
 *   │ Actual ₹22.62L (40.2%) · Pace ₹44.5K/day · Need ₹78.2K/day      │
 *   │ Push ₹33.6K/day extra for 43 days to land Q1.                   │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Period toggle: Q1 (default) ↔ Annual.
 *
 * Sits between the Gap Screamer and the Honest Status Strip.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';

const fINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const VERDICT = {
  on_pace:  { color: '#0F766E', bg: '#E8F7F4', border: '#BAE7DC', label: 'On pace · projected to land', emoji: 'on-pace' },
  catchup:  { color: '#92400E', bg: '#FEF6E4', border: '#FACC15', label: 'Catch-up needed',              emoji: 'catchup' },
  behind:   { color: '#A32D2D', bg: '#FCEBEB', border: '#E8B4B4', label: 'Behind pace · gap widening',   emoji: 'behind' },
  no_data:  { color: '#6B7280', bg: '#F7F5F2', border: '#E5E1DC', label: 'No revenue captured · trailing 7d', emoji: 'no-data' },
};

export default function TargetPaceLifebar({ divisionId, divisionName }) {
  const [period, setPeriod] = useState('quarterly');
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Only TDB has the leader-pulse endpoint so far; other divisions
      // can fall back to the existing quarter-stats (no trailing-7d).
      if (divisionId === 'tdb') {
        const r = await api.get('/tdb/leader-pulse');
        setPulse(r.data.pace);
      } else {
        const [q, y] = await Promise.all([
          api.get(`/divisions/${divisionId}/quarter-stats?period=quarterly`),
          api.get(`/divisions/${divisionId}/quarter-stats?period=ytd`),
        ]);
        // Adapt legacy quarter-stats shape into the pace contract.
        const adapt = (d, label) => ({
          period: label === 'Q1' ? 'quarterly' : 'annual',
          label,
          target: d.quarter_target,
          actual: d.quarter_revenue,
          actual_pct: d.quarter_pct,
          days_in_period: d.days_in_period,
          days_elapsed: d.days_elapsed,
          days_remaining: d.days_remaining,
          daily_needed_to_close: d.daily_run_rate_needed,
          weekly_needed_to_close: d.weekly_run_rate_needed,
          pace_per_day_trailing_7d: null,
          projected_close: null,
          projected_gap: null,
          gap_to_target: Math.max(0, (d.quarter_target || 0) - (d.quarter_revenue || 0)),
          expected_today: (d.days_elapsed / d.days_in_period) * (d.quarter_target || 0),
          expected_today_pct: (d.days_elapsed / d.days_in_period) * 100,
          verdict: 'no_data',
          verdict_label: 'Pace metric not yet wired for this division',
          daily_extra_vs_pace: null,
        });
        setPulse({ quarterly: adapt(q.data, 'Q1'), annual: adapt(y.data, y.data.period_label) });
      }
    } catch (e) {
      // 403 (non-division user) → render nothing rather than scare with red.
      if (e?.response?.status === 403) {
        setPulse({ forbidden: true });
      } else {
        setError(e?.response?.data?.detail || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [divisionId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !pulse || pulse.forbidden) return null;
  if (error) {
    return (
      <div className="rounded-md px-3 py-2 text-xs"
        style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}
        data-testid="pace-lifebar-error">
        Pace bar error: {error}
      </div>
    );
  }
  const data = pulse[period];
  if (!data) return null;
  const v = VERDICT[data.verdict] || VERDICT.no_data;
  const actualPct = Math.max(0, Math.min(100, data.actual_pct || 0));
  const expectedPct = Math.max(0, Math.min(100, data.expected_today_pct || 0));

  return (
    <div className="rounded-[10px] overflow-hidden"
      style={{ backgroundColor: v.bg, border: `1.5px solid ${v.border}` }}
      data-testid="target-pace-lifebar"
      data-verdict={data.verdict}>
      {/* Top row · period toggle + headline */}
      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Target size={15} style={{ color: v.color }} />
          <span className="text-sm font-semibold" style={{ color: v.color }} data-testid="pace-headline">
            {data.label} Pace · {v.label}
          </span>
          {data.projected_close != null && data.projected_close > 0 && (
            <span className="text-[12px]" style={{ color: v.color, opacity: 0.9 }}>
              · projected {fINR(data.projected_close)} vs target {fINR(data.target)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          <button onClick={() => setPeriod('quarterly')}
            className={`px-2 py-0.5 rounded-full ${period === 'quarterly' ? 'font-semibold text-white' : 'text-[#6B7280] hover:bg-white/40'}`}
            style={period === 'quarterly' ? { backgroundColor: v.color } : { border: `1px solid ${v.border}` }}
            data-testid="pace-toggle-quarterly">Q1</button>
          <button onClick={() => setPeriod('annual')}
            className={`px-2 py-0.5 rounded-full ${period === 'annual' ? 'font-semibold text-white' : 'text-[#6B7280] hover:bg-white/40'}`}
            style={period === 'annual' ? { backgroundColor: v.color } : { border: `1px solid ${v.border}` }}
            data-testid="pace-toggle-annual">Annual</button>
        </div>
      </div>

      {/* Progress bar with expected-here marker */}
      <div className="relative px-4 mb-2">
        <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}>
          <div className="h-full rounded-full transition-all"
            style={{ width: `${actualPct}%`, backgroundColor: v.color }}
            data-testid="pace-bar-actual" />
        </div>
        {/* Expected-here tick */}
        <div className="absolute top-0 -translate-x-1/2"
          style={{ left: `calc(${expectedPct}% + 1rem)` }}
          title={`Expected by today: ${expectedPct.toFixed(1)}%`}
          data-testid="pace-bar-expected-tick">
          <div className="w-px h-3.5 mt-2" style={{ backgroundColor: '#1B2A4A' }} />
          <span className="text-[9px] absolute -translate-x-1/2 mt-0.5 whitespace-nowrap"
            style={{ color: '#1B2A4A', left: '0' }}>
            should-be
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pb-3 text-[11px]">
        <StatCell
          label="Realised"
          value={fINR(data.actual)}
          sub={`${data.actual_pct}% of ${fINR(data.target)}`}
          accent={v.color}
          testid="pace-stat-realised"
        />
        <StatCell
          label="Pace · trailing 7d"
          value={data.pace_per_day_trailing_7d != null ? `${fINR(data.pace_per_day_trailing_7d)}/day` : 'pending'}
          sub={data.trailing_7d_revenue != null ? `${fINR(data.trailing_7d_revenue)} last 7d` : ''}
          accent="#185FA5"
          testid="pace-stat-pace"
        />
        <StatCell
          label="Needed to close"
          value={`${fINR(data.daily_needed_to_close)}/day`}
          sub={`${data.days_remaining} days left`}
          accent={v.color}
          testid="pace-stat-needed"
        />
        <StatCell
          label="Gap to target"
          value={fINR(data.gap_to_target)}
          sub={`${data.days_elapsed}/${data.days_in_period} days elapsed`}
          accent="#1B2A4A"
          testid="pace-stat-gap"
        />
      </div>

      {/* Action line */}
      {data.daily_extra_vs_pace != null && data.daily_extra_vs_pace > 0 && (
        <div className="px-4 pb-3 text-[12px] flex items-center gap-1.5 flex-wrap"
          style={{ color: v.color }} data-testid="pace-action-line">
          {data.verdict === 'behind' ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
          <span className="font-medium">
            Push <strong>{fINR(data.daily_extra_vs_pace)}/day extra</strong> for the next {data.days_remaining} days
            to land {data.label}. (Current pace: {fINR(data.pace_per_day_trailing_7d)}/day · need {fINR(data.daily_needed_to_close)}/day)
          </span>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-sm font-bold tabular-nums leading-tight" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] tabular-nums leading-tight" style={{ color: '#6B7280' }}>{sub}</p>}
    </div>
  );
}
